import cx from 'classnames';
import {Button, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {$isLinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$findMatchingParent, mergeRegister} from '@lexical/utils';
import {
    $getSelection,
    $isRangeSelection,
    LexicalEditor,
    COMMAND_PRIORITY_CRITICAL,
    COMMAND_PRIORITY_LOW,
    SELECTION_CHANGE_COMMAND
} from 'lexical';
import React, {Dispatch, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {getSelectedNode} from '../../utils/getSelectedNode';
import {setFloatingElemPositionForLinkEditor} from './utils';
import {OPEN_LINK_MODAL_COMMAND} from '../../commands';
import {texts} from '../../texts';
import styles from './LinkEditor.module.scss';

function FloatingLinkEditor({
    editor,
    isLink,
    setIsLink,
    anchorElem
}: {
    editor: LexicalEditor;
    isLink: boolean;
    setIsLink: Dispatch<boolean>;
    anchorElem: HTMLElement;
}) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [linkUrl, setLinkUrl] = useState('');

    const updateLinkEditor = useCallback(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent)) {
                setLinkUrl(parent.getURL());
            } else if ($isLinkNode(node)) {
                setLinkUrl(node.getURL());
            } else {
                setLinkUrl('');
            }
        }
        const editorElem = editorRef.current;
        const nativeSelection = window.getSelection();

        if (editorElem === null) return null;

        const rootElement = editor.getRootElement();

        if (
            selection !== null &&
            nativeSelection !== null &&
            rootElement !== null &&
            rootElement.contains(nativeSelection.anchorNode) &&
            editor.isEditable()
        ) {
            const domRect: DOMRect | undefined =
                nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
            if (domRect) {
                domRect.y += 30;
                setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
            }
        }
        return true;
    }, [anchorElem, editor]);

    useEffect(() => {
        const update = () => {
            editor.getEditorState().read(() => {
                updateLinkEditor();
            });
        };

        window.addEventListener('resize', update);
        window.addEventListener('scroll', update);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update);
        };
    }, [anchorElem.parentElement, editor, updateLinkEditor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    updateLinkEditor();
                });
            }),

            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    updateLinkEditor();
                    return true;
                },
                COMMAND_PRIORITY_LOW
            )
        );
    }, [editor, updateLinkEditor, setIsLink, isLink]);

    useEffect(() => {
        editor.getEditorState().read(() => {
            updateLinkEditor();
        });
    }, [editor, updateLinkEditor]);

    const unlink = () => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);

    const editLink = () => editor.dispatchCommand(OPEN_LINK_MODAL_COMMAND, null);

    const openLink = () => {
        if (linkUrl) {
            window.open(linkUrl, '_blank').focus();
        }
    };

    return (
        <div ref={editorRef} className={cx(styles.priorityLinkEditor, {[styles.visible]: isLink})}>
            {!isLink ? null : (
                <div className={styles.content}>
                    <span className={styles.btn} title={linkUrl}>
                        <Button
                            size={'small'}
                            skin={'text'}
                            onClick={editLink}
                            prefixIcon={
                                <Icon icon={PriorityIcon.editFill} componentClasses={styles.icon} />
                            }
                        >
                            {texts.edit_link_label}
                        </Button>
                    </span>

                    <div className={styles.divider} />

                    <span className={styles.btn} title={texts.unlink_label}>
                        <Button
                            onClick={openLink}
                            size={'small'}
                            skin={'text'}
                            icon={
                                <Icon
                                    icon={PriorityIcon.externalLink}
                                    componentClasses={styles.icon}
                                />
                            }
                        />
                    </span>

                    <div className={styles.divider} />

                    <span className={styles.btn} title={texts.unlink_label}>
                        <Button
                            size={'small'}
                            skin={'text'}
                            onClick={unlink}
                            icon={
                                <Icon icon={PriorityIcon.ohSnap} componentClasses={styles.icon} />
                            }
                        />
                    </span>
                </div>
            )}
        </div>
    );
}

function useFloatingLinkEditorToolbar(
    editor: LexicalEditor,
    anchorElem: HTMLElement,
    isLink: boolean
) {
    const [activeEditor, setActiveEditor] = useState(editor);

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            $findMatchingParent(node, $isLinkNode);
        }
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                (_payload, newEditor) => {
                    updateToolbar();
                    setActiveEditor(newEditor);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL
            )
        );
    }, [editor, updateToolbar]);

    return createPortal(
        <FloatingLinkEditor
            editor={activeEditor}
            isLink={isLink}
            anchorElem={anchorElem}
            setIsLink={e => {}}
        />,
        anchorElem
    );
}

export function FloatingLinkEditorPlugin({isLink}: {isLink: boolean}) {
    const [editor] = useLexicalComposerContext();

    return useFloatingLinkEditorToolbar(editor, document.body, isLink);
}
